/**
 * Data versioning and migrations.
 *
 * THREE INDEPENDENT VERSION LAYERS
 * -------------------------------
 *   appVersion      package.json      code, features, bug fixes
 *   Dexie schema    db.version(n)     tables and indexes
 *   dataVersion     settings          CONTENT SEMANTICS
 *
 * The third is the one that matters for correctness. A document created
 * under dataVersion 1 must render under dataVersion 1 rules forever, so the
 * version is stamped on the document itself.
 *
 * MIGRATION RULES
 * ---------------
 *  1. Every migration declares the tables it touches, and runs inside a
 *     transaction spanning EXACTLY those tables. An undeclared table is an
 *     error, not a silent partial write.
 *  2. The version stamp is written in the same transaction as the migration
 *     body, so there is no state in which a migration half-applied.
 *  3. A migration is NEVER destructive without `allowDestructive`, which
 *     requires an explicit pre-migration snapshot from the caller.
 *  4. Migrations are tested against a fixture database at the PRIOR version.
 */

import { z } from 'zod';
import type { DNRDatabase } from './database';
import { seedSequenceFromDocuments } from '../repositories/sequences';

export const DATA_VERSION_KEY = 'app:dataVersion';

export type MigrationTable =
  | 'businesses'
  | 'parties'
  | 'categories'
  | 'taxRates'
  | 'products'
  | 'stockLocations'
  | 'documents'
  | 'documentLines'
  | 'documentLinks'
  | 'payments'
  | 'paymentAllocations'
  | 'expenses'
  | 'stockTransactions'
  | 'documentSequences'
  | 'settings'
  | 'auditLogs'
  | 'attachments'
  | 'userAccounts'
  | 'priceLists'
  | 'priceListItems'
  // v2, cloud sync. Additive: no business table is rewritten by a migration, so
  // an existing ledger crosses a schema upgrade untouched.
  | 'syncOutbox'
  | 'syncState'
  | 'devices'
  | 'syncConflicts';

export interface Migration {
  readonly dataVersion: number;
  readonly name: string;
  readonly description: string;
  /** Every table the body reads or writes. Drives the transaction scope. */
  readonly tables: readonly MigrationTable[];
  /** true only if this migration can lose data. Requires a snapshot. */
  readonly allowDestructive: boolean;
  up: (db: DNRDatabase) => Promise<void>;
}

/**
 * Re-derive sequences from the documents actually present.
 *
 * Runs after any import. The counters in a 1.6.2 export are "0" and {}, so
 * MAX(docNumberSeq) is the only trustworthy source. `seedSequenceFromDocuments`
 * never lowers a sequence, so this is safe to run repeatedly.
 */
export const rederiveSequencesMigration: Migration = {
  dataVersion: 2,
  name: 'rederive-sequences',
  description: 'Recompute document sequences from the highest number actually issued.',
  tables: ['businesses', 'documents', 'documentSequences', 'settings'],
  allowDestructive: false,
  up: async (db) => {
    // NOTE: this must be a plain call on an ALREADY-LOADED module. A dynamic
    // wait import() here would yield to the microtask queue, Dexie would
    // see no pending operation, and the transaction would auto-commit --
    // leaving the version stamp outside the migration it belongs to.
    // A Dexie transaction must never await a non-Dexie promise.
    const businesses = await db.businesses.toCollection().primaryKeys();
    for (const businessId of businesses) {
      await seedSequenceFromDocuments(db, businessId);
    }
  },
};

/**
 * Backfill syncChangeId on entity rows from matching outbox entries.
 *
 * WHY THIS MIGRATION EXISTS
 * -------------------------
 * Before the syncChangeId fix in recordBusinessMutation.ts, local writes did
 * NOT set syncChangeId on the entity row. Self-echo detection therefore relied
 * SOLELY on a lookup in syncOutbox: "is this changeId still pending here?"
 *
 * That signal is fragile: once an outbox entry is acked and pruned, the lookup
 * fails for that change forever. The second signal — `existing.syncChangeId ===
 * change.changeId` — was the missing fallback, but only works if syncChangeId
 * was actually written to the row.
 *
 * This migration walks every acked outbox entry and, when the entry's version
 * matches the entity row's current syncVersion, copies the changeId into
 * syncChangeId. The version match is the safety gate: it ensures we never set
 * syncChangeId from a stale outbox entry that does not reflect the row's
 * current state. Rows that already have syncChangeId are left untouched.
 */
const ENTITY_TABLE_FOR_MIGRATION: Record<string, string> = {
  party: 'parties',
  product: 'products',
  category: 'categories',
  taxRate: 'taxRates',
  stockLocation: 'stockLocations',
  document: 'documents',
  documentLine: 'documentLines',
  documentLink: 'documentLinks',
  payment: 'payments',
  paymentAllocation: 'paymentAllocations',
  expense: 'expenses',
  stockTransaction: 'stockTransactions',
  business: 'businesses',
};

export const backfillSyncChangeIdMigration: Migration = {
  dataVersion: 3,
  name: 'backfill-sync-change-id',
  description:
    'Set syncChangeId on entity rows from matching acked outbox entries, ' +
    'enabling safe outbox pruning and robust self-echo detection.',
  tables: [
    'businesses',
    'parties',
    'products',
    'categories',
    'taxRates',
    'stockLocations',
    'documents',
    'documentLines',
    'documentLinks',
    'payments',
    'paymentAllocations',
    'expenses',
    'stockTransactions',
    'syncOutbox',
    'settings',
  ],
  allowDestructive: false,
  up: async (db) => {
    // Only acked entries: pending entries may not yet have their final version
    // committed to the entity row, so matching on version would be unreliable.
    const acked = await db.syncOutbox.where('status').equals('acked').toArray();

    // Group by entity type so we only touch tables that actually have entries.
    const byType = new Map<string, typeof acked>();
    for (const entry of acked) {
      const arr = byType.get(entry.entityType) ?? [];
      arr.push(entry);
      byType.set(entry.entityType, arr);
    }

    for (const [entityType, entries] of byType) {
      const tableName = ENTITY_TABLE_FOR_MIGRATION[entityType];
      if (!tableName) continue;
      const table = db.table(tableName);

      for (const entry of entries) {
        const row = await table.get(entry.entityId);
        if (!row) continue;
        const syncVersion = (row as { syncVersion?: number }).syncVersion;
        const syncChangeId = (row as { syncChangeId?: string }).syncChangeId;
        // Only backfill when the outbox entry's version matches the row's
        // current version AND the row does not already have a syncChangeId.
        // This prevents overwriting a newer syncChangeId from a stale entry.
        if (syncVersion === entry.version && !syncChangeId) {
          await table.update(entry.entityId, { syncChangeId: entry.changeId });
        }
      }
    }
  },
};

export const MIGRATIONS: readonly Migration[] = [
  {
    dataVersion: 1,
    name: 'baseline',
    description: 'Initial schema. No data transformation required.',
    tables: ['settings'],
    allowDestructive: false,
    up: async () => {
      // Nothing to do. The schema itself is the migration.
    },
  },
  rederiveSequencesMigration,
  backfillSyncChangeIdMigration,
];

export const LATEST_DATA_VERSION = MIGRATIONS.reduce((max, m) => Math.max(max, m.dataVersion), 0);

export type MigrationErrorCode =
  'MIGRATION_FAILED' | 'VERSION_AHEAD' | 'MISSING_SNAPSHOT' | 'INCOMPLETE_CHAIN';

export class MigrationError extends Error {
  readonly code: MigrationErrorCode;
  override readonly cause?: unknown;

  constructor(code: MigrationErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'MigrationError';
    this.code = code;
    this.cause = cause;
  }
}

export interface MigrationResult {
  from: number;
  to: number;
  applied: number[];
  durationMs: number;
}

/** Read the stored data version, defaulting to 0 for a fresh database. */
export async function readDataVersion(db: DNRDatabase): Promise<number> {
  const row = await db.settings.get(DATA_VERSION_KEY);
  const parsed = z.coerce
    .number()
    .int()
    .min(0)
    .safeParse(row?.value ?? 0);
  return parsed.success ? parsed.data : 0;
}

async function writeDataVersion(db: DNRDatabase, version: number): Promise<void> {
  await db.settings.put({
    id: DATA_VERSION_KEY,
    scope: 'app',
    scopeId: 'app',
    key: 'dataVersion',
    value: version,
    updatedAt: new Date(0).toISOString(),
  });
}

/** Validate the chain itself, before running anything. */
export function validateChain(chain: readonly Migration[]): MigrationError | null {
  const versions = chain.map((m) => m.dataVersion).sort((a, b) => a - b);
  if (versions.length === 0) return null;
  if (versions[0] !== 1) {
    return new MigrationError(
      'INCOMPLETE_CHAIN',
      'The migration chain must start at data version 1.',
    );
  }
  for (let i = 1; i < versions.length; i++) {
    const previous = versions[i - 1];
    const current = versions[i];
    if (previous === undefined || current === undefined) continue;
    if (current !== previous + 1) {
      return new MigrationError(
        'INCOMPLETE_CHAIN',
        `The migration chain skips from version ${previous} to ${current}.`,
      );
    }
  }
  for (const m of chain) {
    if (m.tables.length === 0) {
      return new MigrationError(
        'INCOMPLETE_CHAIN',
        `Migration ${m.dataVersion} (${m.name}) declares no tables.`,
      );
    }
  }
  return null;
}

/**
 * Apply every pending migration in the chain, in order, each in its own
 * transaction spanning exactly the tables it declared.
 *
 * `chain` is a parameter so tests can exercise failure and rollback paths
 * without mutating the real MIGRATIONS array.
 */
export async function runMigrationChain(
  db: DNRDatabase,
  chain: readonly Migration[] = MIGRATIONS,
): Promise<MigrationResult> {
  const started = Date.now();

  const invalid = validateChain(chain);
  if (invalid) throw invalid;

  const from = await readDataVersion(db);
  const latest = chain.reduce((max, m) => Math.max(max, m.dataVersion), 0);

  if (from > latest) {
    throw new MigrationError(
      'VERSION_AHEAD',
      `This database is at data version ${from}, but this build only understands ${latest}. ` +
        'The file was created by a newer version of DNR Vyapar.',
    );
  }

  const pending = chain
    .filter((m) => m.dataVersion > from)
    .sort((a, b) => a.dataVersion - b.dataVersion);

  if (pending.some((m) => m.allowDestructive)) {
    throw new MigrationError(
      'MISSING_SNAPSHOT',
      'A pending migration is destructive. Take a verified backup before migrating.',
    );
  }

  const applied: number[] = [];

  for (const migration of pending) {
    // The scope MUST cover every table the body touches, plus settings for
    // the version stamp. Dexie rejects a write to a table outside the scope,
    // which is what turns a partial migration into an all-or-nothing one.
    const scope = [...new Set([...migration.tables, 'settings' as MigrationTable])].map((name) =>
      db.table(name),
    );

    try {
      await db.transaction('rw', scope, async () => {
        await migration.up(db);
        await writeDataVersion(db, migration.dataVersion);
      });
      applied.push(migration.dataVersion);
    } catch (error) {
      throw new MigrationError(
        'MIGRATION_FAILED',
        `Migration ${migration.dataVersion} (${migration.name}) failed: ${
          error instanceof Error ? error.message : String(error)
        }. The database is unchanged and still at version ${from}.`,
        error,
      );
    }
  }

  if (pending.length === 0 && from === 0) {
    // Fresh database: stamp the current version so we do not re-scan.
    await db.transaction('rw', [db.settings], () => writeDataVersion(db, latest));
  }

  return { from, to: latest, applied, durationMs: Date.now() - started };
}

/** Run the shipped chain. This is what the app calls on boot. */
export function runMigrations(db: DNRDatabase): Promise<MigrationResult> {
  return runMigrationChain(db, MIGRATIONS);
}
